<script setup lang="ts" generic="T extends { id: string }">
import draggable from 'vuedraggable';
import { reactive } from 'vue';
import { NxtIconButton, NxtLink, NxtIcon } from '@future-harry/nxt-styles';

interface Props {
   addButtonLabel: string;
}

interface Emits {
   add: [];
   remove: [id: string];
}

const items = defineModel<T[]>({ required: true });
const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const collapsed = reactive<Record<string, boolean>>({});
const toggleRow = (id: string) => {
   collapsed[id] = !collapsed[id];
};
</script>

<template>
   <div class="draggable-form-list">
      <draggable
         v-if="items.length"
         class="draggable-form-list__rows"
         v-model="items"
         :handle="`.draggable-form-list__row-header`"
         item-key="id"
      >
         <template #item="{ element: item, index }">
            <div>
               <div class="draggable-form-list__row-header">
                  <span>
                     <NxtIcon
                        :name="collapsed[item.id] ? 'chevron-right' : 'chevron-down'"
                        size="1rem"
                        color="var(--nxt-white)"
                        @click.stop="toggleRow(item.id)"
                     />
                     <slot name="title" :item="item" :index="index">
                        <h4>Section {{ index + 1 }}</h4>
                     </slot>
                  </span>
                  <NxtIconButton
                     name="trash"
                     color="var(--nxt-white)"
                     hover-color="var(--nxt-dark-grey)"
                     @click="$emit('remove', item.id)"
                  />
               </div>
               <div v-show="!collapsed[item.id]" class="draggable-form-list__fields">
                  <slot name="fields" :item="item" :index="index" />
               </div>
            </div>
         </template>
      </draggable>
      <NxtLink class="nxt-link--add-row" @click="$emit('add')">
         <NxtIcon name="plus" size="1rem" />
         {{ addButtonLabel }}
      </NxtLink>
   </div>
</template>

<style scoped lang="scss">
.draggable-form-list {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter);

   &__rows {
      display: flex;
      flex-direction: column;
      gap: var(--nxt-gutter-large);
   }

   &__row-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: var(--nxt-medium-dark);
      border-radius: var(--nxt-radius);
      padding: 0 var(--nxt-gutter);
      cursor: move;

      span {
         display: flex;
         align-items: center;
         gap: var(--nxt-gutter);
      }

      :deep(h4),
      h4 {
         margin: 0;
         display: inline;
         color: var(--nxt-white);
      }
   }

   &__fields {
      display: flex;
      flex-direction: column;
      gap: var(--nxt-gutter);
      margin-top: var(--nxt-gutter);
   }

   .nxt-link--add-row {
      color: var(--nxt-blue);
      text-align: left;

      i {
         color: var(--nxt-blue);
      }
   }
}
</style>
